import type { FC } from "react"
import { useCallback, useEffect, useState } from "react"
import { observer } from "mobx-react-lite"

import type { ManualClPositionStore } from "@/domains/thick-v2/positions/manual-cl-position-store"
import type { PoolOhlcStore } from "@/domains/thick-v2/stores/pool-ohlc-store"
import { tickToPriceString } from "@/domains/manual-cl-core"

import { detectOrientation } from "../create-position/price-history-composite"
import { PriceHistoryWithRangeView } from "../create-position/price-history-with-range-view"

export interface ManagePositionPriceHistoryCompositeProps {
  positionStore: ManualClPositionStore
  poolOhlcStore: PoolOhlcStore
  /** Optional override for visual density; view defaults to 120 px tall. */
  height?: number
  className?: string
}

/**
 * Snapshot of the price-history chart with the position's tick bounds drawn
 * as a read-only band — so on the Overview tab the user can see the range
 * they picked at mint time sitting on top of the pool's actual traded
 * history. The band is derived from the NFP's immutable
 * `[tickLower, tickUpper]` rather than user input, so there are no editing
 * affordances wired up here; the 24h/7d/30d window tabs and the zoom
 * buttons on the view stay functional because they're display-only and
 * genuinely useful for eyeballing "did the pool spend much time in my
 * range?".
 *
 * Mirrors {@link PriceHistoryComposite} so the two surfaces share the same
 * orientation-detection heuristic (the indexer's OHLC orientation is
 * per-pool and sometimes the reciprocal of `tickToPriceString`'s canonical
 * orientation; we reuse `detectOrientation` to pick the right one).
 */
const PRICE_ZOOM_STEP = 1.6
const PRICE_ZOOM_MIN = 0.1
const PRICE_ZOOM_MAX = 10

export const ManagePositionPriceHistoryComposite: FC<ManagePositionPriceHistoryCompositeProps> =
  observer(({ positionStore, poolOhlcStore, height, className }) => {
    const pool = positionStore.pool
    const poolAddress = pool?.address.toViemAddress() ?? undefined
    const [zoomFactor, setZoomFactor] = useState(1)

    const handleZoomIn = useCallback(() => {
      setZoomFactor((prev) => Math.max(PRICE_ZOOM_MIN, prev / PRICE_ZOOM_STEP))
    }, [])
    const handleZoomOut = useCallback(() => {
      setZoomFactor((prev) => Math.min(PRICE_ZOOM_MAX, prev * PRICE_ZOOM_STEP))
    }, [])

    useEffect(() => {
      poolOhlcStore.setActivePool(poolAddress)
      return () => {
        // Only release the store if this composite is the one that
        // claimed the pool — mirrors the create-side composite so siblings
        // sharing the same OHLC singleton don't clear each other.
        if (poolOhlcStore.activePoolAddress === poolAddress?.toLowerCase()) {
          poolOhlcStore.setActivePool(undefined)
        }
      }
    }, [poolAddress, poolOhlcStore])

    if (!pool) return null

    const rawMin = parseFloatOrUndefined(
      tryTickToPrice({
        tick: positionStore.tickLower,
        pool,
      }),
    )
    const rawMax = parseFloatOrUndefined(
      tryTickToPrice({
        tick: positionStore.tickUpper,
        pool,
      }),
    )
    const rawCurrent = parseFloatOrUndefined(pool.displayMidPrice)

    const orientation = detectOrientation({
      closeValues: poolOhlcStore.points
        .map((point) => point.close ?? point.open)
        .filter((value): value is number => value !== undefined && Number.isFinite(value)),
      referenceValue: rawCurrent,
    })

    const minPrice = orient(rawMin, orientation)
    const maxPrice = orient(rawMax, orientation)
    const currentPrice = orient(rawCurrent, orientation)

    const [sortedMin, sortedMax] =
      minPrice !== undefined && maxPrice !== undefined && minPrice > maxPrice
        ? [maxPrice, minPrice]
        : [minPrice, maxPrice]

    return (
      <PriceHistoryWithRangeView
        points={poolOhlcStore.points}
        window={poolOhlcStore.window}
        minPrice={sortedMin}
        maxPrice={sortedMax}
        currentPrice={currentPrice}
        isInRange={positionStore.isInRange}
        isLoading={poolOhlcStore.isLoading}
        onWindowChange={(next) => poolOhlcStore.setWindow(next)}
        baseSymbol={pool.token0.symbol}
        quoteSymbol={pool.token1.symbol}
        height={height}
        className={className}
        zoomFactor={zoomFactor}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        canZoomIn={zoomFactor > PRICE_ZOOM_MIN}
        canZoomOut={zoomFactor < PRICE_ZOOM_MAX}
      />
    )
  })

ManagePositionPriceHistoryComposite.displayName =
  "ManagePositionPriceHistoryComposite"

function tryTickToPrice({
  tick,
  pool,
}: {
  tick: number
  pool: ManualClPositionStore["pool"]
}): string | undefined {
  try {
    return tickToPriceString({
      tick,
      token0: pool.token0,
      token1: pool.token1,
      decimalPlaces: 6,
    })
  } catch {
    return undefined
  }
}

function orient(
  value: number | undefined,
  orientation: "as-is" | "inverted",
): number | undefined {
  if (value === undefined || !Number.isFinite(value) || value <= 0) return value
  return orientation === "inverted" ? 1 / value : value
}

function parseFloatOrUndefined(value: string | undefined): number | undefined {
  if (value === undefined || value === "") return undefined
  const parsed = parseFloat(value)
  return Number.isFinite(parsed) ? parsed : undefined
}
